import bindAllOfThis from "src/utils/BindThisHelper";
import { PhaserButton, PhaserButtonConfig } from './Button';
import { Color } from "src/games/shared/ColorValue";
import { PhaserModalBase, ModalOptionBase, PhaserModalConfigBase } from "../../components/common/PhaserModalBase";
import { numKeys } from "src/constants/KeyboardNumKeys";

export class ModalOption extends ModalOptionBase {
}

export class PhaserModalConfig extends PhaserModalConfigBase {
    public title?: string;
    public buttonWidth: number = 200;
    public buttonConfig: PhaserButtonConfig = new PhaserButtonConfig();
    public borderColor?: number = 0xffffff;
}

export class PhaserModal extends PhaserModalBase {
    protected background: Phaser.GameObjects.Image;
    protected border: Phaser.GameObjects.Image;
    protected title: Phaser.GameObjects.Text;
    protected columns: boolean = false;
    protected buttonHeight: number;
    protected padding = 10;
    config: PhaserModalConfig;
    buttons: PhaserButton[];

    constructor(scene: Phaser.Scene, x: number, y: number, options: ModalOption[], config?: PhaserModalConfig) {
        super(scene, x, y, config);
        this.options = options;
        this.config = config;

        if (config == null) {
            this.config = new PhaserModalConfig();
        }

        this.buttonHeight = 30;
        if(this.options.length === 1) {
            this.config.buttonConfig.backgroundColor = Color.KUDER_PRIMARY_BUTTON;
        }
        else {
            this.config.buttonConfig.backgroundColor = Color.KUDER_SECONDARY_BUTTON;
        }
        bindAllOfThis(this, PhaserModal.prototype);
        this.initialize();
        this.setScrollFactor(0);
    }

    static preload(scene: Phaser.Scene) {
        scene.load.image("pixel", require('/src/assets/qci/sprites/pixel.png'));
    }

    initialize(): void {
        var height;
        var width;

        if (this.config.title != null) {
            this.createTitle();
            height = this.title.getBottomLeft().y + (this.buttonHeight + 10) * (this.options.length + 1);
            width = 800;
        }
        else {
            height = this.buttonHeight * (this.options.length + 2);
            width = this.config.buttonWidth * 2;
        }

        this.setSize(width, height);

        // Construct Objects
        this.constructObjects();

        this.type = "PhaserModal";

        // Add this modal to the scene
        this.scene.add.existing(this);
        this.bindNumKeys();
    }

    /**
     * Adjusts the modal's size based on the sizes of the title and buttons within
     * Also adjusts to two columns if needed.
     */
    adjustModalSizeBasedOnItems(): void {
        var height;
        var width;

        if (this.options.length > 4) {
            this.columns = true;
        }

        if (this.config.buttonConfig.smartSize) {
            var maxButtonHeight = 0;
            var maxButtonWidth = 0;
            this.buttons.forEach((b: PhaserButton) => {
                if (b.getIdealButtonHeight() >= maxButtonHeight) {
                    maxButtonHeight = b.getIdealButtonHeight();
                }
                if (b.getIdealButtonWidth() >= maxButtonWidth) {
                    maxButtonWidth = b.getIdealButtonWidth();
                }
            });

            this.config.buttonWidth = maxButtonWidth;
            this.buttonHeight = maxButtonHeight;

            this.buttons.forEach((b: PhaserButton) => {
                b.setDisplaySize(this.config.buttonWidth, this.buttonHeight);
            });
        }

        // Set height and width to be sufficient to hold all options
        if (this.config.title != null) {
            var optionSpace;
            if (this.options.length === 1) {
                optionSpace = this.buttonHeight * 2.5;
                width = Math.max(this.config.buttonWidth * 2, 3 * this.title.width / 4);
            }
            else if (!this.columns) {
                optionSpace = (this.buttonHeight + this.padding) * (this.options.length + 1);
                width = Math.max(this.config.buttonWidth * 2, 3 * this.title.width / 4);
            }
            else {
                optionSpace = (this.buttonHeight + this.padding) * Math.ceil(this.options.length / 2 + 1);
                width = Math.max(this.config.buttonWidth * 3, 3 * this.title.width / 4);
            }
            height = this.title.getBottomLeft().y + optionSpace;
        }
        else {
            if (!this.columns) {
                height = (this.buttonHeight + this.padding) * (this.options.length + 2);
                width = this.config.buttonWidth * 2;
            }
            else {
                height = (this.buttonHeight + this.padding) * Math.ceil(this.options.length / 2 + 2);
                width = this.config.buttonWidth * 3;
            }
        }

        this.setSize(width, height);

        // Now reset background and title positions and sizes
        this.background.setDisplaySize(this.width - this.padding, this.height - this.padding);
        this.border.setDisplaySize(this.width, this.height);
        if (this.config.title != null) {
            this.title.setWordWrapWidth(width - 2 * this.padding, true);
            this.title.setX(width / 2 - this.title.width / 2);
        }
    }

    /**
     * Create the buttons and at the same time keep track of the maximum buttonHeight
    */
    createButtons(): Array<PhaserButton> {
        var buttons = [];
        let index: number = 0;
        this.options.forEach((op: ModalOption) => {
            var button = new PhaserButton(this.scene, 0, 0, this.config.buttonWidth, this.buttonHeight, numKeys[index].display + ': ' + op.displayText, this.config.buttonConfig);
            button.on('buttonClicked', (pointer: Phaser.Input.Pointer, x: number, y: number) => {
                if(this.scene.sound != null) {
                    this.scene.sound.stopAll();
                }
                this.emit('optionClicked', op, button);
            });
            buttons.push(button);
            index++;
        });
        return buttons;
    }

    /**
    * Go through already created buttons and adjust their sizes and positions (because now we will use the correct buttonHeight)
    */
    adjustButtons(): void {
        var y;
        if (this.config.title != null) {
            y = this.title.getBottomLeft().y;
        }
        else {
            y = this.buttonHeight + this.padding;
        }

        var buttonIndex = 0;
        this.buttons.forEach((button: PhaserButton) => {
            var buttonX = this.getXCoordinate(buttonIndex, button);

            // Restart y value for second column
            if (this.columns && buttonIndex === Math.ceil(this.options.length / 2)) {
                if (this.config.title != null) {
                    y = this.title.getBottomLeft().y;
                }
                else {
                    y = this.buttonHeight + this.padding;
                }
            }

            // Adjust position
            button.setPosition(buttonX, y);
            button.setDisplaySize(this.config.buttonWidth, this.buttonHeight);
            button.height = this.buttonHeight;

            this.add(button);

            y += this.buttonHeight + this.padding;
            buttonIndex++;
        });
    }

    /**
    *  Helper method to get the x coordinate for the button based on if there are columns or not
    */
    getXCoordinate(buttonIndex: number, button: PhaserButton): number {
        var buttonX;
        // First column
        if (this.columns && buttonIndex < Math.ceil(this.options.length / 2)) {
            buttonX = this.width / 4 - this.config.buttonWidth / 4;
        }
        // Second column
        else if (this.columns && buttonIndex >= Math.ceil(this.options.length / 2)) {
            buttonX = this.config.buttonWidth + this.padding + this.width / 4 - button.width / 4;
        }
        // No columns (in the middle)
        else {
            buttonX = this.width / 2 - this.config.buttonWidth / 2;
        }

        return buttonX;
    }

    constructObjects(): void {
        // Border of the modal
        this.border = this.scene.add.image(-this.padding / 2, -this.padding / 2, "pixel");
        this.border.setOrigin(0);
        this.border.setTint(this.config.borderColor);
        this.border.setDisplaySize(this.width, this.height);
        this.add(this.border);

        // Background of the modal
        this.background = this.scene.add.image(0, 0, "pixel");
        this.background.setDisplaySize(this.width, this.height);
        this.background.setOrigin(0, 0);
        this.background.setTint(0x000000);
        this.add(this.background);

        // Size has changed so adjust title positioning
        if (this.config.title != null) {
            this.title.setWordWrapWidth(this.width, true);
            this.title.setX(this.width / 2 - this.title.width / 2);
            this.add(this.title);
        }

        this.buttons = this.createButtons();
        // Reset the modal size because buttonHeight was adjusted
        this.adjustModalSizeBasedOnItems();
        // Reset button positions and sizes
        this.adjustButtons();
    }

    createTitle(): void {
        this.title = this.scene.add.text(0, 0, this.config.title);
        this.title.setOrigin(0, 0);
        this.title.setTint(0xffffff);
        this.title.setFontSize(20);
        this.title.setFontFamily('Russo One, Arial');
        this.title.setPadding(15, 15, 15, 15);
        this.title.setAlign('center');
        this.title.setWordWrapWidth(800 - 2 * this.padding, true);
        this.title.setX(this.width / 2 - this.title.width / 2);
    }
}



// WEBPACK FOOTER //
// ./src/games/shared/Modal.ts